import { useEffect } from 'react';
import { Grid, Typography, SxProps, Theme, FormHelperText } from '@mui/material';
import { useSnackbar } from 'notistack';
import { getProductRowKey } from 'quoting/new-quote/helpers';
import { AddProductTable } from 'quoting/new-quote/tables/products-table';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Product } from 'shared/types/quotes';

const alertStyles: SxProps<Theme> = {
  border: '1px solid #0288d1',
  borderRadius: '5px',
  color: '#0288d1',
  backgroundColor: '#e8f4fd',
  padding: 1,
  marginBottom: 1,
};

interface IProductInfoProps {
  dateSubmitted?: string | null;
}

export const ProductsInfo = ({
  dateSubmitted
}: IProductInfoProps) => {
  const {
    watch,
    formState: { errors },
    setValue,
    clearErrors,
    getValues,
  } = useFormContext();
  const { enqueueSnackbar } = useSnackbar();
  const products = getValues('products') as Product[];
  const [rows, setRows] = React.useState<Product[]>(products ?? []);

  const watchStatus = watch('status');
  const isAbleToDeleteProducts =
    watchStatus === 'IN PROCESS' ||
    watchStatus === 'INCOMPLETE INFORMATION' ||
    watchStatus === 'NO QUOTE' ||
    !watchStatus;

  const handleAddRow = (newRow: Product) => {
    newRow.createdDate = new Date().toISOString();
    setRows(prevState => {
      const existingRow = prevState.find(currentRow => getProductRowKey(currentRow) === getProductRowKey(newRow));
      if (!existingRow) {
        return [...prevState, newRow];
      }

      enqueueSnackbar(`This product is already listed. `, { variant: 'warning' });
      return [...prevState];
    });
    clearErrors('products');
  };

  const handleRemoveRow = (row: Product) =>
    setRows(prevState => {
      const existingRowIndex = prevState.findIndex(
        currentRow => getProductRowKey(currentRow) === getProductRowKey(row)
      );
      if (existingRowIndex < 0) {
        enqueueSnackbar(`Could not remove product.`, { variant: 'error' });
        return prevState;
      }
      const newRows = [...prevState];
      newRows.splice(existingRowIndex, 1);
      return [...newRows];
    });

  useEffect(() => {
    setValue('products', rows);
  }, [rows, setValue]);

  return (
    <Grid container sx={{ marginTop: 2, marginBottom: 2 }}>
      <Grid item>
        <Typography variant='h6'>Product Information</Typography>
        <Typography sx={alertStyles} variant='body2'>
          Click add button to save product information before moving to next section.
        </Typography>
      </Grid>
      <AddProductTable
        rows={rows}
        handleAddRow={handleAddRow}
        handleRemoveRow={handleRemoveRow}
        dateSubmitted={dateSubmitted}
        isAbleToDeleteProducts={isAbleToDeleteProducts}
      />
      <FormHelperText sx={{ margin: 1 }} error>
        {errors?.products?.message}
      </FormHelperText>
    </Grid>
  );
};
